import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { PageViewedTracker } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';

import AssistantWelcome from '#client/components/AssistantWelcome';
import ChatConnectionError from '#client/components/ChatConnectionError';
import Transition from '#client/components/Transition';
import { CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';
import { getWarmStartConfig, isValidWarmStartCode } from '#client/constants/warmStartMessages';
import { useUnifiedBusinessEntityContext } from '#client/contexts/UnifiedBusinessEntityProvider';
import { useThreadCreation } from '#client/hooks';
import useUserMode from '#client/hooks/useUserMode';
import { validateQueryMessage } from '#client/utils/validateQueryMessage';

/**
 * UniversalThreadCreator Component
 *
 * Universal component for creating new threads across all user modes and routes.
 *
 * Supports:
 * - All user modes: anonymous, starter, business, standalone
 * - Two creation patterns: auto-create (immediate) or user-initiated (welcome screen)
 * - Warm start via ?topic=xyz URL parameter
 * - Message param via ?message=text URL parameter
 * - Retry logic with exponential backoff
 * - Comprehensive error handling
 *
 * Auto-create Conditions (shows loading, skips welcome screen):
 * 1. autoCreate prop is explicitly true (legacy behavior)
 * 2. User mode is business (business users skip welcome)
 * 3. Valid ?topic=xyz query param present
 * 4. ?message=text query param present
 *
 * Welcome Screen Conditions (shows AssistantWelcome):
 * - None of the above conditions are met
 * - Typically: anonymous or starter mode without query params
 *
 * Routes:
 * - /guide/company/:businessEntityUuid (legacy, auto-create via prop, business only)
 * - /guide/chat (new, auto-create computed from conditions, all modes)
 *
 * @param {object} props - Component props
 * @param {boolean} props.autoCreate - If true, creates thread immediately on mount (legacy)
 *                                     If false or omitted, computed from conditions (recommended)
 * @returns {React.ReactElement} The UniversalThreadCreator component
 */
export default function UniversalThreadCreator({ autoCreate = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { businessEntityUuid: urlBusinessEntityUuid } = useParams();

  // Context and hooks
  const { loading: entityLoading, businessEntityUuid: contextBusinessEntityUuid } =
    useUnifiedBusinessEntityContext();
  const {
    createNewThread,
    loading: threadLoading,
    error: threadError,
    customError
  } = useThreadCreation();

  const { value: enableVelo, loading: enableVeloLoading } = useExperimentFlag(
    'enable-velo-assistant',
    false,
    true
  );

  const { value: enableMultiVeloMode, loading: enableMultiVeloModeLoading } = useExperimentFlag(
    'enable-multi-velo-mode',
    false,
    true
  );

  const userMode = useUserMode();

  // Use either error from mutation or custom error from response
  const effectiveError = threadError || customError;

  // Refs
  const activePromptInputRef = useRef(null);
  const threadCreationInitiated = useRef(false);

  // Warm start support
  const topicCode = searchParams.get('topic');
  const isValidTopic = topicCode && isValidWarmStartCode(topicCode);

  // Validate message query parameter
  const rawMessage = searchParams.get('message');
  const messageValidation = useMemo(() => {
    if (!rawMessage) return { isValid: true, message: null, error: null };

    const result = validateQueryMessage(rawMessage);

    if (!result.isValid) {
      console.warn('[UniversalThreadCreator] Invalid message param:', {
        error: result.error,
        rawLength: rawMessage?.length,
        rawPreview: rawMessage?.substring(0, 50)
      });
    }

    if (result.warnings) {
      console.warn('[UniversalThreadCreator] Message has warnings:', {
        warnings: result.warnings.map((w) => w.code),
        messagePreview: result.message?.substring(0, 100)
      });
    }

    return result;
  }, [rawMessage]);

  const validatedMessage = messageValidation.isValid ? messageValidation.message : null;

  // Determine which businessEntityUuid to use (URL param takes precedence for legacy route)
  const effectiveBusinessEntityUuid = urlBusinessEntityUuid || contextBusinessEntityUuid;

  // Compute whether to auto-create based on explicit prop OR conditions
  const shouldAutoCreate = useMemo(() => {
    // Explicit prop takes precedence
    if (autoCreate) return true;

    // Otherwise check conditions:
    // 1. Business mode users get auto-create
    if (userMode.isBusiness) return true;

    // 2. Valid topic param triggers auto-create
    if (isValidTopic) return true;

    // 3. Valid message param triggers auto-create
    if (validatedMessage) return true;

    // 4. Default to welcome screen
    return false;
  }, [autoCreate, userMode.isBusiness, isValidTopic, validatedMessage]);

  // Local state
  const [showWelcome, setShowWelcome] = useState(!shouldAutoCreate);
  const [retryCount, setRetryCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Sync showWelcome with shouldAutoCreate when it changes (e.g., when userMode loads)
  useEffect(() => {
    setShowWelcome(!shouldAutoCreate);
  }, [shouldAutoCreate]);

  // Reset thread creation flag when business entity changes
  useEffect(() => {
    threadCreationInitiated.current = false;
  }, [effectiveBusinessEntityUuid]);

  /* 
    User can send topic or message
   */

  /**
   * Build input for thread creation (warm start or default)
   */
  const getThreadInput = useCallback(
    (customMessage = null) => {
      // Priority: custom message from welcome screen > query param message > warm start > default

      // 1. Custom message from user input (welcome screen)
      if (customMessage) {
        return {
          messageType: 'text',
          message: customMessage,
          role: 'user'
        };
      }

      // 2. Validated message from query param
      if (validatedMessage) {
        return {
          messageType: 'text',
          message: validatedMessage,
          role: 'user'
        };
      }

      // 3. Warm start from topic
      if (isValidTopic && effectiveBusinessEntityUuid) {
        return getWarmStartConfig(topicCode, effectiveBusinessEntityUuid);
      }

      // 4. Default greeting
      return {
        messageType: 'greet',
        message: 'greet',
        role: 'user'
      };
    },
    [validatedMessage, isValidTopic, topicCode, effectiveBusinessEntityUuid]
  );

  /**
   * Handle thread creation
   * Note: createNewThread automatically navigates to thread viewer on success
   */
  const handleCreateThread = useCallback(
    async (input) => {
      if (isProcessing) return;

      try {
        setIsProcessing(true);
        // createNewThread automatically navigates to thread viewer
        // It takes (overrideBusinessEntityUuid, initialInput)
        await createNewThread(effectiveBusinessEntityUuid, input);

        // Success! Reset retry count
        setRetryCount(0);
      } catch (err) {
        console.error('[UniversalThreadCreator] Thread creation failed:', err);
        // Error will be displayed via threadError from hook
      } finally {
        setIsProcessing(false);
      }
    },
    [createNewThread, effectiveBusinessEntityUuid, isProcessing]
  );

  /**
   * Handle retry with backoff
   */
  const handleRetry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
    threadCreationInitiated.current = false; // Reset flag to allow retry

    const input = getThreadInput();
    handleCreateThread(input);
  }, [getThreadInput, handleCreateThread]);

  /**
   * Auto-create thread on mount (for shouldAutoCreate mode)
   */
  useEffect(
    () => {
      if (enableVeloLoading || enableMultiVeloModeLoading) {
        return;
      }
      if (!enableMultiVeloMode && !userMode.isBusiness) {
        window.location.replace('/');
        return;
      }
      if (!enableVelo) return;
      if (!shouldAutoCreate) return;
      if (entityLoading) return;
      if (threadLoading) return;
      if (effectiveError) return;
      if (threadCreationInitiated.current) return;

      // Mark as initiated to prevent duplicate calls
      threadCreationInitiated.current = true;

      const input = getThreadInput();
      handleCreateThread(input);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      enableMultiVeloMode,
      shouldAutoCreate,
      enableVelo,
      entityLoading,
      threadLoading,
      effectiveError,
      getThreadInput,
      handleCreateThread,
      effectiveBusinessEntityUuid,
      topicCode,
      isValidTopic
    ]
  );

  /**
   * Handle message send from welcome screen
   */
  const handleMessageSend = useCallback(
    async (messageText) => {
      setShowWelcome(false);
      const input = getThreadInput(messageText);
      await handleCreateThread(input);
    },
    [getThreadInput, handleCreateThread]
  );

  /**
   * Handle prompt selection from welcome screen
   */
  const handlePromptSelect = useCallback((promptText) => {
    if (activePromptInputRef.current?.setValue) {
      activePromptInputRef.current.setValue(promptText);
    }
  }, []);

  // Show error for invalid message query parameter
  if (rawMessage && !messageValidation.isValid) {
    return (
      <ChatConnectionError
        instructions={[]}
        type="VALIDATION_ERROR"
        onRetry={() => navigate('/guide/chat', { replace: true })}
        onStartNew={() => navigate('/guide/chat', { replace: true })}
      />
    );
  }

  // Show entity loading state
  if (entityLoading) {
    return <Transition type="ENTITY_LOADING" />;
  }

  // Show error state with retry options
  if (effectiveError) {
    return (
      <ChatConnectionError
        instructions={[]}
        onRetry={handleRetry}
        onStartNew={() => navigate('/guide/chat', { replace: true })}
      />
    );
  }

  // Show thread creation loading state (for auto-create mode)
  if ((threadLoading || isProcessing) && shouldAutoCreate) {
    return (
      <Transition
        type={retryCount > 0 ? 'THREAD_CREATING_RETRY' : 'THREAD_CREATING'}
        typeProps={{ retryCount }}
      />
    );
  }

  // Show welcome screen (for user-initiated mode)
  if (showWelcome) {
    return (
      <>
        <PageViewedTracker {...CLICKSTREAM_COMMON_PROPS} appPageName="thread_creator" />
        <AssistantWelcome
          activePromptInputRef={activePromptInputRef}
          disabled={threadLoading || isProcessing}
          onMessageSend={handleMessageSend}
          onPromptSelect={handlePromptSelect}
        />
      </>
    );
  }

  // Fallback (should rarely be reached)
  return <Transition type="ALMOST_READY" />;
}

UniversalThreadCreator.propTypes = {
  autoCreate: PropTypes.bool
};
